import { useEffect, useState } from 'react';

export interface GooglePlaceResult {
  formatted_address?: string;
}

export interface GoogleAutocomplete {
  addListener: (eventName: string, handler: () => void) => void;
  getPlace: () => GooglePlaceResult | undefined;
}

export interface GoogleMapsPlacesNamespace {
  Autocomplete: new (
    input: HTMLInputElement,
    opts: { types: string[]; fields?: string[] },
  ) => GoogleAutocomplete;
}

export interface GoogleMapsNamespace {
  places: GoogleMapsPlacesNamespace;
}

export interface GoogleNamespace {
  maps: GoogleMapsNamespace;
}

interface UseGoogleMapsPlacesOptions {
  apiKey?: string;
  libraries?: string[];
}

declare global {
  interface Window {
    google?: GoogleNamespace;
  }
}

const isPlacesReady = (): boolean => Boolean(window.google?.maps?.places);

export function useGoogleMapsPlaces(options: UseGoogleMapsPlacesOptions = {}) {
  const {
    apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
    libraries = ['places'],
  } = options;
  const [loaded, setLoaded] = useState<boolean>(isPlacesReady());
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (loaded) return;

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-wa-google-maps]',
    );
    if (existing) {
      if (isPlacesReady()) {
        setLoaded(true);
        return;
      }
      const handleLoad = () => setLoaded(isPlacesReady());
      const handleError = () =>
        setError(new Error('Failed to load Google Maps script'));
      existing.addEventListener('load', handleLoad);
      existing.addEventListener('error', handleError);
      return () => {
        existing.removeEventListener('load', handleLoad);
        existing.removeEventListener('error', handleError);
      };
    }

    if (!apiKey) {
      setError(
        new Error('Missing Google Maps API key (VITE_GOOGLE_MAPS_API_KEY)'),
      );
      return;
    }

    const params = new URLSearchParams({
      key: apiKey,
      libraries: libraries.join(','),
    });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-wa-google-maps', 'true');

    const handleLoad = () => setLoaded(isPlacesReady());
    const handleError = () =>
      setError(new Error('Failed to load Google Maps script'));
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [apiKey, libraries, loaded]);

  return { loaded, error };
}
